import { Stack, Text, Textarea } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ScoreScale } from "./ScoreScale";

interface Props {
  points: number | null;
  comment: string;
  isReadOnly: boolean;
  onPointsChange: (value: number | null) => void;
  onCommentChange: (value: string) => void;
  size?: "md" | "sm";
}

/**
 * The buyer's verdict on one award criterion: a score out of ten and the justification behind it,
 * sitting under the supplier's answer so the two are read together. Injected into the answer card's
 * footer slot, the way QualificationReviewCard injects its comment field.
 */
export const AwardCriterionScoreFields = ({
  points,
  comment,
  isReadOnly,
  onPointsChange,
  onCommentChange,
  size = "md",
}: Props) => {
  const { t } = useTranslation();

  return (
    <Stack spacing="3">
      <ScoreScale value={points} onChange={onPointsChange} isReadOnly={isReadOnly} size={size} />
      {/* The justification is stored with the score and cannot outlive it, so the field says what it
          is waiting for rather than adding a line of explanation under every unscored row. */}
      {isReadOnly ? (
        comment ? (
          <Text fontSize="sm" color="smText" whiteSpace="pre-wrap" maxW="82ch">
            {comment}
          </Text>
        ) : null
      ) : (
        <Textarea
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
          placeholder={String(points == null ? t("Give a score to write a justification") : t("Justification..."))}
          rows={3}
          resize="vertical"
          isDisabled={points == null}
          // Chakra's focusBorderColor resolves raw colours, not semantic tokens, and its default
          // focus state stacks a 1px ring on top of the 1px border — two lines reading as one thick
          // one. The border alone carries it, in the same green the grade strip takes on.
          // Chakra's own hover for an input resolves to the same grey it already has, so the field
          // never answers the cursor. Set here, at the weight the grade strip above uses.
          _hover={{ borderColor: "smBackgroundTertiary" }}
          _focusVisible={{ borderColor: "smSuccess", boxShadow: "none" }}
        />
      )}
    </Stack>
  );
};
